import {
  RichContentBlock,
  RichContentDocument,
  RichListItem,
  RichTextMark,
  RichTextSpan,
  RichTableRow,
  RichTableCell
} from '../api/blogTypes';

const HEADING_ANCHOR = /\s*\{#([A-Za-z0-9_-]+)\}\s*$/;
const IMAGE_PATTERN = /^!\[(.*?)]\((\S+?)(?:\s+"([^"]*)")?\)\s*$/;

type InlineToken = {
  text: string;
  marks?: RichTextMark[];
  href?: string;
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const pushPlain = (segments: InlineToken[], buffer: string): void => {
  if (buffer.trim().length === 0) {
    return;
  }
  segments.push({ text: buffer });
};

const pushSegment = (
  segments: InlineToken[],
  text: string,
  mark?: RichTextMark,
  href?: string
): void => {
  if (text.length === 0) {
    return;
  }
  const marks: RichTextMark[] = [];
  if (mark) {
    marks.push(mark);
  }
  if (href) {
    if (href.startsWith('#')) {
      marks.push('anchor');
    } else {
      marks.push('link');
    }
  }
  const token: InlineToken = { text };
  if (marks.length > 0) {
    token.marks = Array.from(new Set(marks));
  }
  if (href) {
    token.href = href;
  }
  segments.push(token);
};

const parseInline = (value: string): RichTextSpan[] => {
  if (!value.trim()) {
    return [];
  }
  const segments: InlineToken[] = [];
  let buffer = '';
  let index = 0;
  const text = value;

  const flushBuffer = (mark?: RichTextMark, marker = ''): ((end: number) => void) => {
    return (end: number) => {
      const content = text.slice(index + marker.length, end);
      pushPlain(segments, buffer);
      buffer = '';
      pushSegment(segments, content, mark);
      index = end + marker.length;
    };
  };

  while (index < text.length) {
    const remaining = text.slice(index);
    if (remaining.startsWith('**') || remaining.startsWith('__') || remaining.startsWith('//')) {
      const marker = remaining.slice(0, 2);
      const end = text.indexOf(marker, index + 2);
      if (end !== -1) {
        flushBuffer('bold', marker)(end);
        continue;
      }
    }
    if (remaining.startsWith('*') || remaining.startsWith('_')) {
      const marker = remaining.slice(0, 1);
      const end = text.indexOf(marker, index + 1);
      if (end !== -1) {
        flushBuffer('italic', marker)(end);
        continue;
      }
    }
    if (remaining.startsWith('~~')) {
      const end = text.indexOf('~~', index + 2);
      if (end !== -1) {
        flushBuffer('strike', '~~')(end);
        continue;
      }
    }
    if (remaining.startsWith('`')) {
      const end = text.indexOf('`', index + 1);
      if (end !== -1) {
        flushBuffer('code', '`')(end);
        continue;
      }
    }
    if (remaining.startsWith('[')) {
      const closing = text.indexOf(']', index + 1);
      const openParen = closing !== -1 ? text.indexOf('(', closing) : -1;
      const closeParen = openParen !== -1 ? text.indexOf(')', openParen) : -1;
      if (closing > index && openParen === closing + 1 && closeParen > openParen) {
        pushPlain(segments, buffer);
        buffer = '';
        const label = text.slice(index + 1, closing);
        const href = text.slice(openParen + 1, closeParen);
        pushSegment(segments, label, undefined, href);
        index = closeParen + 1;
        continue;
      }
    }
    buffer += text[index];
    index += 1;
  }

  pushPlain(segments, buffer);
  return segments.map((segment) => ({
    text: segment.text,
    marks: segment.marks,
    href: segment.href,
  }));
};

export const parseInlineMarkdown = (value: string): RichTextSpan[] => parseInline(value);

const parseListItem = (line: string): string => line.replace(/^\s*([*\-+]\s+|\d+\.\s+)/, '').trim();

const parseBlockLines = (lines: string[], start: number): [RichContentBlock, number] | null => {
  let index = start;
  let line = lines[index];
  if (!line || !line.trim()) {
    return null;
  }

  if (line.startsWith('#')) {
    const level = Math.min(6, line.match(/^#+/)?.[0].length ?? 1);
    let text = line.slice(level).trim();
    let anchor: string | undefined;
    const match = text.match(HEADING_ANCHOR);
    if (match) {
      anchor = match[1];
      text = text.slice(0, text.lastIndexOf(match[0])).trim();
    }
    const children = parseInline(text);
    return [
      { type: 'heading', level, children, anchor },
      index + 1,
    ];
  }

  if (line.startsWith('>')) {
    const collected: string[] = [];
    while (index < lines.length && lines[index].startsWith('>')) {
      collected.push(lines[index].slice(1).trim());
      index += 1;
    }
    return [
      { type: 'quote', children: parseInline(collected.join('\n').trim()) },
      index,
    ];
  }

  if (/^(?:\s*[\-*+]\s+|\s*\d+\.\s+)/.test(line)) {
    const ordered = /^\s*\d+\.\s+/.test(line);
    const items: RichListItem[] = [];
    while (index < lines.length && /^(?:\s*[\-*+]\s+|\s*\d+\.\s+)/.test(lines[index])) {
      const text = parseListItem(lines[index]);
      items.push({ children: parseInline(text) });
      index += 1;
    }
    return [
      { type: 'list', style: ordered ? 'ordered' : 'unordered', items },
      index,
    ];
  }

  const imageMatch = line.trim().match(IMAGE_PATTERN);
  if (imageMatch) {
    return [
      {
        type: 'image',
        alt: imageMatch[1],
        src: imageMatch[2],
        caption: imageMatch[3] ?? undefined,
      },
      index + 1,
    ];
  }

  const paragraphLines: string[] = [];
  while (index < lines.length && lines[index].trim()) {
    paragraphLines.push(lines[index].trim());
    index += 1;
  }
  const text = paragraphLines.join('\n').trim();
  if (!text) {
    return null;
  }
  return [{ type: 'paragraph', children: parseInline(text) }, index];
};

export const parseMarkdownToContent = (markdown: string | null | undefined): RichContentBlock[] => {
  if (!markdown) {
    return [];
  }
  const normalized = markdown.replace(/\r/g, '');
  const lines = normalized.split('\n');
  const blocks: RichContentBlock[] = [];
  let index = 0;
  while (index < lines.length) {
    const current = lines[index];
    if (!current.trim()) {
      index += 1;
      continue;
    }
    const result = parseBlockLines(lines, index);
    if (!result) {
      index += 1;
      continue;
    }
    const [block, nextIndex] = result;
    blocks.push(block);
    index = nextIndex;
  }
  return blocks;
};

const renderInline = (spans: RichTextSpan[]): string =>
  spans
    .map((span) => {
      let text = escapeHtml(span.text).replace(/\n/g, '<br />');
      const marks = span.marks ?? [];
      if (marks.includes('code')) {
        text = `<code>${text}</code>`;
      }
      if (marks.includes('bold')) {
        text = `<strong>${text}</strong>`;
      }
      if (marks.includes('italic')) {
        text = `<em>${text}</em>`;
      }
      if (marks.includes('strike')) {
        text = `<s>${text}</s>`;
      }
      if (span.href) {
        const href = escapeHtml(span.href);
        text = `<a href="${href}">${text}</a>`;
      }
      return text;
    })
    .join('');

const resolveImageSrc = (
  src: string,
  resolver?: (filename: string) => string
): string => {
  if (resolver) {
    return resolver(src);
  }

  if (/^https?:\/\//i.test(src) || src.startsWith('/')) {
    return src;
  }

  return `/images/${src}`;
};

const renderBlocksToHtml = (
  content: RichContentBlock[],
  imageResolver?: (filename: string) => string
): string =>
  content
    .map((block) => {
      switch (block.type) {
        case 'heading': {
          const level = Math.min(6, Math.max(1, block.level));
          const anchor = block.anchor ? ` id="${escapeHtml(block.anchor)}"` : '';
          return `<h${level}${anchor}>${renderInline(block.children)}</h${level}>`;
        }
        case 'paragraph':
          return `<p>${renderInline(block.children)}</p>`;
        case 'quote':
          return `<blockquote>${renderInline(block.children)}</blockquote>`;
        case 'list': {
          const wrapper = block.style === 'ordered' ? ['<ol>', '</ol>'] : ['<ul>', '</ul>'];
          const items = block.items
            .map((item) => `<li>${renderInline(item.children)}</li>`)
            .join('');
          return `${wrapper[0]}${items}${wrapper[1]}`;
        }
        case 'image': {
          const alt = escapeHtml(block.alt ?? '');
          const caption = block.caption ? `<figcaption>${escapeHtml(block.caption)}</figcaption>` : '';
          const src = resolveImageSrc(block.src, imageResolver);
          return `<figure><img src="${escapeHtml(src)}" alt="${alt}" loading="lazy" decoding="async" />${caption}</figure>`;
        }
        case 'table': {
          const rows = block.rows
            .map((row) => {
              const cells = row.cells
                .map((cell) => {
                  const tag = cell.type === 'header' ? 'th' : 'td';
                  return `<${tag}>${renderInline(cell.children)}</${tag}>`;
                })
                .join('');
              return `<tr>${cells}</tr>`;
            })
            .join('');
          return `<table>${rows}</table>`;
        }
        default:
          return '';
      }
    })
    .join('');

export const renderContentToHtml = (
  content: RichContentBlock[],
  options?: { resolveImageSrc?: (filename: string) => string }
): string => renderBlocksToHtml(content, options?.resolveImageSrc);

export const renderContentDocumentToHtml = (
  document: RichContentDocument,
  options?: { resolveImageSrc?: (filename: string) => string }
): string => renderBlocksToHtml(document.blocks, options?.resolveImageSrc);

export const ensureContent = (
  candidate: RichContentBlock[] | null | undefined,
  markdown: string | null | undefined
): RichContentBlock[] => {
  if (candidate && Array.isArray(candidate) && candidate.length > 0) {
    return candidate;
  }
  return parseMarkdownToContent(markdown ?? '');
};

export const createEmptyContentDocument = (): RichContentDocument => ({ blocks: [] });

export const createContentDocumentFromBlocks = (
  blocks: RichContentBlock[] = []
): RichContentDocument => ({
  blocks: Array.isArray(blocks) ? [...blocks] : []
});

export const ensureContentDocument = (
  candidate: RichContentDocument | null | undefined,
  markdown: string | null | undefined
): RichContentDocument => {
  const blocks = ensureContent(candidate?.blocks, markdown);
  return { blocks };
};

export const extractPlainText = (content: RichContentBlock[]): string => {
  const parts: string[] = [];
  const append = (spans: RichTextSpan[]): void => {
    spans.forEach((span) => {
      if (span.text.trim()) {
        parts.push(span.text.trim());
      }
    });
  };

  content.forEach((block) => {
    switch (block.type) {
      case 'heading':
      case 'paragraph':
      case 'quote':
        append(block.children);
        break;
      case 'list':
        block.items.forEach((item) => append(item.children));
        break;
      default:
        break;
    }
  });

  return parts.join(' ');
};

const escapeMarkdownText = (text: string): string =>
  text
    .replace(/\\/g, '\\\\')
    .replace(/([*_`~\[\](){}>#+])/g, '\\$1');  // Removed hyphen - not needed in middle of text

const stripZeroWidthCharacters = (text: string): string =>
  text.replace(/[\u200B\u200C\u200D\u2060\uFEFF]/g, '');

const isSpanMeaningful = (span: RichTextSpan): boolean =>
  stripZeroWidthCharacters(span.text).length > 0;

const pruneInlineSpans = (spans: RichTextSpan[]): RichTextSpan[] =>
  spans.filter((span) => isSpanMeaningful(span));

const pruneBlock = (block: RichContentBlock): RichContentBlock | null => {
  switch (block.type) {
    case 'paragraph': {
      const children = pruneInlineSpans(block.children);
      if (children.length === 0) {
        return null;
      }
      return { ...block, children };
    }
    case 'heading': {
      const children = pruneInlineSpans(block.children);
      if (children.length === 0) {
        return null;
      }
      return { ...block, children };
    }
    case 'quote': {
      const children = pruneInlineSpans(block.children);
      if (children.length === 0) {
        return null;
      }
      return { ...block, children };
    }
    case 'list': {
      const items = block.items
        .map((item) => {
          const children = pruneInlineSpans(item.children);
          if (children.length === 0) {
            return null;
          }
          return { ...item, children };
        })
        .filter((item): item is RichListItem => item !== null);
      if (items.length === 0) {
        return null;
      }
      return { ...block, items };
    }
    case 'image':
      return block;
    default:
      return block;
  }
};

export const pruneEmptyContentBlocks = (blocks: RichContentBlock[]): RichContentBlock[] =>
  blocks
    .map((block) => pruneBlock(block))
    .filter((block): block is RichContentBlock => block !== null);

export const pruneEmptyContentDocument = (
  document: RichContentDocument
): RichContentDocument => ({
  blocks: pruneEmptyContentBlocks(document.blocks)
});

const spansToMarkdown = (spans: RichTextSpan[]): string =>
  spans
    .map((span) => {
      const marks = span.marks ?? [];
      const href = span.href ?? undefined;
      let output = escapeMarkdownText(span.text);

      if (marks.includes('code')) {
        return `\`${output}\``;
      }

      if (marks.includes('bold')) {
        output = `**${output}**`;
      }
      if (marks.includes('italic')) {
        output = `*${output}*`;
      }
      if (marks.includes('strike')) {
        output = `~~${output}~~`;
      }

      if ((marks.includes('link') || marks.includes('anchor')) && href) {
        output = `[${output}](${href})`;
      }

      return output;
    })
    .join('');

export const stringifySpansToMarkdown = (spans: RichTextSpan[]): string => spansToMarkdown(spans);

export const serializeContentToMarkdown = (content: RichContentBlock[]): string =>
  pruneEmptyContentBlocks(content)
    .map((block) => {
      switch (block.type) {
        case 'paragraph':
          return spansToMarkdown(block.children);
        case 'heading': {
          const level = Math.min(6, Math.max(1, block.level));
          const anchor = block.anchor ? ` {#${block.anchor}}` : '';
          const text = spansToMarkdown(block.children);
          return `${'#'.repeat(level)} ${text}${anchor}`.trim();
        }
        case 'quote': {
          const text = spansToMarkdown(block.children);
          return text
            .split('\n')
            .map((line) => `> ${line}`.trimEnd())
            .join('\n');
        }
        case 'list': {
          return block.items
            .map((item, index) => {
              const prefix = block.style === 'ordered' ? `${index + 1}.` : '-';
              const itemText = spansToMarkdown(item.children);
              return `${prefix} ${itemText}`;
            })
            .join('\n');
        }
        case 'image': {
          const alt = escapeMarkdownText(block.alt ?? '');
          const caption = block.caption?.replace(/"/g, '\\"');
          const title = caption ? ` "${caption}"` : '';
          return `![${alt}](${block.src}${title})`;
        }
        default:
          return '';
      }
    })
    .filter((segment) => segment.trim().length > 0)
    .join('\n\n');

export const serializeContentDocumentToMarkdown = (document: RichContentDocument): string =>
  serializeContentToMarkdown(document.blocks);
